import {
  Briefcase,
  Newspaper,
  Globe,
  UserPlus,
  ClipboardList,
  MessageCircle,
  HeartPulse,
  FileSignature,
  BarChart3,
  Share2,
  Megaphone,
  Zap,
  Mail,
  RotateCcw,
  Handshake,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { SIGNAL_TYPES, ACTION_STATES, type SignalType, type ActionState } from '@shared/schemas/terra-signals';

/**
 * Icon/label/tone lookup for the Actions tab (plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * §2b item 1). Every `signal_type` gets an icon tile + label; a new arm that
 * introduces a 16th type fails typecheck here (`satisfies Record<SignalType,
 * …>`) until it's added — see `.claude/skills/create-terra-signal-source/
 * SKILL.md` Step 4b. `tone` is a Tailwind class string for the icon tile
 * background/foreground, same shape as `AccountTimeline.tsx`'s
 * `KIND_CONFIG[...].tileClass` — semantic tokens only, no raw color literals.
 */
export interface SignalTypeDisplay {
  label: string;
  Icon: LucideIcon;
  tone: string;
}

export const SIGNAL_TYPE_DISPLAY = {
  'Job Change': {
    label: 'Job Change',
    Icon: Briefcase,
    tone: 'bg-tangerine-50 text-tangerine-700 dark:bg-tangerine-900/30 dark:text-tangerine-400',
  },
  News: {
    label: 'News',
    Icon: Newspaper,
    tone: 'bg-nightshade-100 text-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-300',
  },
  'Website Visit': {
    label: 'Website Visit',
    Icon: Globe,
    tone: 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
  },
  'New Hire': {
    label: 'New Hire',
    Icon: UserPlus,
    tone: 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
  },
  'Job Posting': {
    label: 'Job Posting',
    Icon: ClipboardList,
    tone: 'bg-lemon-50 text-lemon-700 dark:bg-lemon-900/30 dark:text-lemon-400',
  },
  Touchpoint: {
    label: 'Touchpoint',
    Icon: MessageCircle,
    tone: 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
  },
  'Account Health': {
    label: 'Account Health',
    Icon: HeartPulse,
    tone: 'bg-pomegranate-50 text-pomegranate-700 dark:bg-pomegranate-900/30 dark:text-pomegranate-400',
  },
  Contract: {
    label: 'Contract',
    Icon: FileSignature,
    tone: 'bg-nightshade-100 text-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-300',
  },
  Consumption: {
    label: 'Consumption',
    Icon: BarChart3,
    tone: 'bg-ube-50 text-ube-700 dark:bg-ube-900/30 dark:text-ube-400',
  },
  'Social Engagement': {
    label: 'Social Engagement',
    Icon: Share2,
    tone: 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
  },
  Campaign: {
    label: 'Campaign',
    Icon: Megaphone,
    tone: 'bg-ube-50 text-ube-700 dark:bg-ube-900/30 dark:text-ube-400',
  },
  'Gong Flow': {
    label: 'Gong Flow',
    Icon: Zap,
    tone: 'bg-lemon-50 text-lemon-700 dark:bg-lemon-900/30 dark:text-lemon-400',
  },
  Message: {
    label: 'Message',
    Icon: Mail,
    tone: 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
  },
  'Open Loop': {
    label: 'Open Loop',
    Icon: RotateCcw,
    tone: 'bg-pomegranate-50 text-pomegranate-700 dark:bg-pomegranate-900/30 dark:text-pomegranate-400',
  },
  'Warm Intro': {
    label: 'Warm Intro',
    Icon: Handshake,
    tone: 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
  },
} satisfies Record<SignalType, SignalTypeDisplay>;

/** Never rendered — the generic fallback for an unknown/null `signal_type`. */
const FALLBACK_DISPLAY: SignalTypeDisplay = {
  label: 'Signal',
  Icon: Sparkles,
  tone: 'bg-bg-secondary text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400',
};

const SIGNAL_TYPE_SET = new Set<string>(SIGNAL_TYPES);

/**
 * Fail-safe resolver: never throws, even on a `signal_type` this table
 * doesn't recognize (a future arm shipped before its display entry landed,
 * or a malformed row). `subtype` is accepted for a future per-subtype
 * override but unused today — every subtype falls back to its type's entry
 * (SKILL.md Step 4b).
 */
export function resolveSignalTypeDisplay(type: string | null, _subtype?: string | null): SignalTypeDisplay {
  if (type != null && SIGNAL_TYPE_SET.has(type)) {
    return SIGNAL_TYPE_DISPLAY[type as SignalType];
  }
  return type ? { ...FALLBACK_DISPLAY, label: type } : FALLBACK_DISPLAY;
}

/** Pill background/text classes per `action_state` — neutral fallback for anything unrecognized. */
export const ACTION_STATE_PILL_CLASS = {
  Insight: 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
  'To do': 'bg-tangerine-50 text-tangerine-700 dark:bg-tangerine-900/30 dark:text-tangerine-400',
  Snoozed: 'bg-bg-secondary text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400',
  Completed: 'bg-content-success/10 text-content-success dark:bg-content-success/20 dark:text-content-success',
  Archived: 'bg-bg-secondary text-content-faint dark:bg-nightshade-800 dark:text-nightshade-600',
  Expired: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
} satisfies Record<ActionState, string>;

const ACTION_STATE_SET = new Set<string>(ACTION_STATES);

const NEUTRAL_PILL_CLASS =
  'bg-bg-secondary text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400';

/** Fail-safe resolver: an unrecognized/null `action_state` gets a neutral pill, never a throw. */
export function resolveActionStatePill(state: string | null): string {
  if (state != null && ACTION_STATE_SET.has(state)) {
    return ACTION_STATE_PILL_CLASS[state as ActionState];
  }
  return NEUTRAL_PILL_CLASS;
}
